import { DateTime } from "luxon";

import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { Patient } from "@/Common/Models/Patient/Patient";
import { Gender } from "@/Common/Models/Patient/PatientEnums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { DateOnly } from "@/Common/Utilities/DateAndTime/DateOnly";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { ExportMode } from "@/ExportableFile/Models/enum/ExportMode";
import { FileTypePatientGrowthChart } from "@/ExportableFile/Models/FileTypes/FileTypePatientGrowthChart";
import { IFaxPrintExportableFileConfig } from "@/ExportableFile/Models/IFaxPrintExportableFileConfig";
import { ExportPatientChartResponse } from "@/Patient/Components/MedicalItems/Immunization/ResponseModels/ExportPatientChartResponse";
import { GrowthChartsCacheData } from "@/Patient/Models/GrowthCharts/GrowthChartsCacheData";
import { ShareableGrowthChartModel } from "@/Patient/Models/GrowthCharts/ShareableGrowthChartModel";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { IGrowthChartUiModel } from "@/Patient/Models/UI/Graph/IGrowthChartUiModel";
import { SendGrowthChartStrategy } from "@/Patient/Services/Portal/Strategies/SendGrowthChartStrategy";
import { SendToPortalPopup } from "@/Portal/Components/SendToPortalPopup";
import { NewVitalSignWithoutSpecialityWarningPopup } from "../VitalSigns/NewVitalSignWithoutSpecialityWarningPopup";
import { VitalSignPopup } from "../VitalSigns/VitalSignPopup";
import { getSelectableCharts, GrowthChartSelectable } from "./UIModels/GrowthChartsSelector";
import { GrowthChartsMainContent } from "./GrowthChartsMainContent";
import { GrowthChartsSettingsBar } from "./GrowthChartsSettingsBar";
import { GrowthChartsSidebar } from "./GrowthChartsSidebar";

interface GrowthChartsProps {
    patient: Patient;
    growthChartUI: IGrowthChartUiModel;
    vitals: Immutable.List<MedicalItemVitalSign>;
    encounters: Immutable.List<Encounter>;
    growthChartCache: GrowthChartsCacheData;
}

class GrowthChartsState {
    public activeChart: GrowthChartSelectable;
    public charts: GrowthChartSelectable[];
    public isSidebarCollapsed: boolean;
    public showAddVitalSignPopup: boolean;
    public isEditedVitalSignForFenton: boolean;
    public editingVitalSign: MedicalItemVitalSign;
    public editingVitalSignReason: string;
    public hasValidPatientGender: boolean;
}

class GrowthCharts extends BaseReactComponent<GrowthChartsProps, GrowthChartsState> {
    private readonly hasServiceSpecializations: boolean = false;

    constructor(props: GrowthChartsProps) {
        super(props);
        this.state = this.buildInitialState(props);
        this.hasServiceSpecializations = sessionStore.getCurrentEmployee()?.getServiceSpecializations()?.any() ?? false;
    }

    public componentDidMount(): void {
        if (!this.props.growthChartUI.get("shareableGrowthChart"))
            sessionStore.dispatch(LocalPatientOperations.GrowthCharts.LoadGrowthChartSharedItem, {
                patientContactId: this.props.patient.IndividualResponse.Contact_id
            });
    }

    public componentDidUpdate(prevProps: GrowthChartsProps): void {
        if (prevProps !== this.props) {
            const unitSystem = this.props.growthChartUI.get("unit");

            const charts = getSelectableCharts(
                this.props.patient.IndividualResponse,
                this.filterVitalSigns(this.props),
                this.props.growthChartCache?.Interpolations,
                this.props.growthChartCache?.ZTable,
                unitSystem
            );

            if (this.props.patient !== prevProps.patient) {
                this.applyPreviousSelectionToDefaultCharts(charts, this.props.patient.IndividualResponse.Contact_id);
            }
            else {
                charts.forEach(chart => {
                    const currentChart = this.state.charts?.find(p => p.chart.id === chart.chart.id);
                    if (currentChart)
                        chart.isChecked = currentChart.isChecked;
                });
            }

            this.mutateState(s => {
                s.charts = charts;
                s.activeChart = s.charts.firstOrDefault();
                s.hasValidPatientGender = prevProps.patient?.Gender() !== Gender.Unspecified;
            });
        }
    }

    public render(): JSX.Element {
        return (
            <div className="base-growth-charts" data-cy="growthCharts">
                {this.props.growthChartUI.get("isPortalPopupOpen") && (
                    <SendToPortalPopup
                        model={this.props.growthChartUI.get("portalLinkedItemModel")}
                        strategy={new SendGrowthChartStrategy()}
                    />
                )}

                {this.state.showAddVitalSignPopup && (
                    this.hasServiceSpecializations ?
                        this.renderAddVitalSignPopup() :
                        this.renderWarningPopupWhenCurrentUserHasNoSpeciality()
                )}
                <div
                    className={this.state.isSidebarCollapsed
                        ? "growth-charts growth-charts--collapsed"
                        : "growth-charts"
                    }
                >
                    {this.state.hasValidPatientGender && (
                        <GrowthChartsSidebar
                            charts={this.state?.charts}
                            onChartSelectionChanged={(id, check) => this.onChartSelectionChanged(id, check)}
                            onGrowthChartCategoryChanged={check => this.onGrowthChartsCategoryChanged(check)}
                            onVitalSignCategoryChanged={check => this.onVitalSignCategoryChanged(check)}
                            onAddVitalSignClicked={() => this.mutateState(s => s.showAddVitalSignPopup = true)}
                        />
                    )}
                    {this.state.hasValidPatientGender && this.renderSlideMenuBar()}
                    {this.renderMainSection()}
                </div>
            </div>
        );
    }

    private buildInitialState(props: GrowthChartsProps): GrowthChartsState {
        const initialState = new GrowthChartsState();
        const unitSystem = props.growthChartUI.get("unit");
        const charts = getSelectableCharts(
            props.patient.IndividualResponse,
            this.filterVitalSigns(props),
            props.growthChartCache?.Interpolations,
            props.growthChartCache?.ZTable,
            unitSystem
        );

        this.applyPreviousSelectionToDefaultCharts(charts, props.patient.IndividualResponse.Contact_id);

        initialState.charts = charts;
        initialState.activeChart = charts.firstOrDefault();
        initialState.isSidebarCollapsed = false;
        initialState.showAddVitalSignPopup = false;
        initialState.hasValidPatientGender = props.patient.Gender() !== Gender.Unspecified;

        return initialState;
    }

    private renderSlideMenuBar(): JSX.Element {
        return (
            <a
                className="slide-menu"
                onClick={() => this.mutateState(
                    s => s.isSidebarCollapsed = !s.isSidebarCollapsed,
                    () => setTimeout(() => {
                        window.dispatchEvent(new Event("resize"));
                    }, 250)
                )}
            >
                <div />
                <div />
                <div />
            </a>
        );
    }

    private renderMainSection(): JSX.Element {
        const {
            Contact_id: patientContactId,
            IsBornPremature: isBornPremature,
            BornPrematureWeeks: bornPrematureWeeks
        } = this.props.patient.IndividualResponse;

        // NOTE: this forces GrowthChartsMainContent to unmount/remount so that this.props.activeChart is never null in GrowthChartsNavigationBar
        const hasAtLeastOneChartSelected = this.state?.charts?.some(chart => chart.isChecked) ?? false;

        return (
            <div className="main-section">
                <GrowthChartsSettingsBar
                    patientContactId={patientContactId}
                    isBornPremature={isBornPremature}
                    bornPrematureWeeks={bornPrematureWeeks}
                    filterStartDate={this.props.growthChartUI.get("startDateFilter")}
                    filterEndDate={this.props.growthChartUI.get("endDateFilter")}
                    unit={this.props.growthChartUI.get("unit")}
                    hasValidPatientGender={this.state.hasValidPatientGender}
                    onExportButtonClicked={() => this.exportCharts()}
                    shareableItem={new ShareableGrowthChartModel(this.props.growthChartUI.get("shareableGrowthChart"))}
                    showPortalButton={(this.props.patient.IsRegisteredToPortal() || cacheStore.ClinicSettings.EnablePortalInvitationFlowV2) && this.state?.charts.filter(chart => chart.isChecked).length > 0}
                    hasSelectedCharts={this.state.charts?.any(chart => chart.isChecked)}
                    onFaxLabelClick={() => this.onFaxClick()}
                    onPortalButtonClick={() => this.onPortalClick()}
                    patientDateOfBirth={this.props.patient?.IndividualResponse?.DateOfBirth}
                />
                {this.state.hasValidPatientGender && hasAtLeastOneChartSelected && (
                    <GrowthChartsMainContent
                        charts={this.state?.charts.filter(chart => chart.isChecked).map(c => c.chart)}
                        cacheData={this.props.growthChartCache}
                        filterStartDate={this.props.growthChartUI.get("startDateFilter")}
                        filterEndDate={this.props.growthChartUI.get("endDateFilter")}
                        unit={this.props.growthChartUI.get("unit")}
                        editExistingVitalSign={(vitalSign, isFenton, reason) => this.mutateState(s => {
                            s.editingVitalSign = vitalSign;
                            s.isEditedVitalSignForFenton = isFenton;
                            s.editingVitalSignReason = reason;
                            s.showAddVitalSignPopup = true;
                        })}
                    />
                )}
            </div>
        );
    }

    private onGrowthChartsCategoryChanged(check: boolean): void {
        this.mutateState(s => s.charts = s.charts.map(c => {
            let chart = new GrowthChartSelectable(c.chart);
            chart.isChecked = c.chart.isVitalSign ? c.isChecked : check;
            return chart;
        }), () => this.updateSelectedCharts());
    }

    private onVitalSignCategoryChanged(check: boolean): void {
        this.mutateState(s => s.charts = s.charts.map(c => {
            let chart = new GrowthChartSelectable(c.chart);
            chart.isChecked = !c.chart.isVitalSign ? c.isChecked : check;
            return chart;
        }), () => this.updateSelectedCharts());
    }

    private onChartSelectionChanged(id: string, check: boolean): void {
        this.mutateState(s => s.charts = s.charts.map(c => {
            let chart = new GrowthChartSelectable(c.chart);
            chart.isChecked = c.chart.id === id ? check : c.isChecked;
            return chart;
        }), () => this.updateSelectedCharts());
    }

    private updateSelectedCharts(): void {
        const selectedCharts: { [chartId: string]: boolean } = {};
        this.state.charts
            .filter(chart => chart.isChecked)
            .map(chart => selectedCharts[chart.chart.id] = true);

        sessionStore.dispatch(LocalPatientOperations.GrowthCharts.SetSelectedCharts, {
            selectedCharts: selectedCharts
        });
    }

    private filterVitalSigns(props: GrowthChartsProps): Immutable.List<MedicalItemVitalSign> {
        const filterStartDate = DateOnly.parseUnlessNull(props.growthChartUI.get("startDateFilter"));
        const filterEndDate = DateOnly.parseUnlessNull(props.growthChartUI.get("endDateFilter"));

        return props.vitals.toArray()
            .filter(x =>
                (!filterStartDate || DateOnly.parseUnlessNull(x.PharmaStartDate) >= filterStartDate)
                && (!filterEndDate || DateOnly.parseUnlessNull(x.PharmaStartDate) <= filterEndDate)
                && !x.EMRTemplateIsInvalid
            ).toImmutable();
    }

    private exportCharts(): void {
        sessionStore.dispatch(LocalPatientOperations.GrowthCharts.ExportPatientCharts, {
            exportRequest: this.getChartData()
        });
    }

    private async onFaxClick(): Promise<void> {
        const response = await Client.postAsync<ExportPatientChartResponse>(
            ServerOperations.Server.Myle_Patient_ExportCharts,
            this.getChartData()
        );

        if (response.isSuccess) {
            sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.FaxPrintExportableFile, {
                config: {
                    exportableFile: this.initFileTypeChartsFile(response.data.FileName),
                    exportMode: ExportMode.Fax,
                    patientContactId: this.props.patient.IndividualResponse.Contact_id,
                    popupTitle: cacheStore.GetString("FaxVitalSignsLabel"),
                    hideSignaturePinControl: true
                } as IFaxPrintExportableFileConfig
            });
        }
    }

    private onPortalClick(): void {
        sessionStore.dispatch(LocalPatientOperations.GrowthCharts.PortalClick, {
            doc: this.props.growthChartUI.get("shareableGrowthChart"),
            chartData: this.getChartData()
        });
    }

    private initFileTypeChartsFile(pdfName: string): FileTypePatientGrowthChart {
        return new FileTypePatientGrowthChart({
            ObjectId: pdfName,
            description: cacheStore.GetString("ChartsLabel"),
            chartId: "",
            date: new Date().toString()
        });
    }

    private getChartData(): object {
        return {
            PatientContactId: this.props.patient.IndividualResponse.Contact_id,
            StartDate: this.props.growthChartUI.get("startDateFilter"),
            EndDate: this.props.growthChartUI.get("endDateFilter"),
            UnitSystem: this.props.growthChartUI.get("unit"),
            GrowthCharts: this.state.charts
                .filter(chart => chart.isChecked && !chart.chart.isVitalSign)
                .map(chart => chart.chart.id),
            VitalSignCharts: this.state.charts
                .filter(chart => chart.isChecked && chart.chart.isVitalSign)
                .map(chart => chart.chart.id)
        };
    }

    private applyPreviousSelectionToDefaultCharts(charts: GrowthChartSelectable[], patientContactId: string): void {
        const selectedCharts = patientStore.GrowthChartUiModels[patientContactId].get("selectedCharts");
        if (!selectedCharts)
            return;

        charts?.forEach(chart => {
            chart.isChecked = !!selectedCharts[chart.chart.id];
        });
    }

    private renderAddVitalSignPopup(): JSX.Element {
        const patient = this.props.patient;
        const todayEncounter = this.state.showAddVitalSignPopup
            ? this.props.encounters
                ?.toArray()
                ?.firstOrDefault(p => {
                    const isToday = LuxonDateTimeParser.parse(p.ConsultationDate).toISODate() === DateTime.local().toISODate();
                    const isEmployeeContributor = p.EncounterRelatedEmployees?.any(p => p.EmployeeId === sessionStore.getCurrentEmployee().ContactFacetId);
                    const isTodayEncounterOpen = p.SignedOffDate == null;

                    return isToday && isEmployeeContributor && isTodayEncounterOpen;
                })
            : null;

        return (
            <VitalSignPopup
                onClose={() => this.mutateState(s => {
                    s.showAddVitalSignPopup = false;
                    s.editingVitalSign = null;
                    s.isEditedVitalSignForFenton = false;
                })}
                patientData={{
                    dateOfBirth: patient?.IndividualResponse?.DateOfBirth,
                    gender: patient?.Gender(),
                    ethnicityKeys: patient?.IndividualResponse?.EthnicityKeys,
                    contactFacetId: patient?.InactivableResponse.Id,
                    contactId: patient?.IndividualResponse.Contact_id,
                    isPremature: patient?.IndividualResponse?.IsBornPremature,
                    bornPrematureWeeks: patient?.IndividualResponse?.BornPrematureWeeks,
                    isFenton: this.state.isEditedVitalSignForFenton
                }}
                isAddingToExistingEncounter={!!todayEncounter}
                growthChartsCache={this.props.growthChartCache}
                vitalSignToEdit={this.state.editingVitalSign}
                vitalSignToEditReaon={!!this.state.editingVitalSign ? this.state.editingVitalSignReason : todayEncounter?.VisitPurpose}
            />
        );
    }

    private renderWarningPopupWhenCurrentUserHasNoSpeciality(): JSX.Element {
        return (
            <NewVitalSignWithoutSpecialityWarningPopup onClose={() => this.mutateState(s => s.showAddVitalSignPopup = false)} />
        );
    }
}

export { GrowthCharts };
